import { useEffect, useState } from 'react';
import {
  RadioAvailabilityStatus,
  createRadioAvailabilityStatus,
  deleteRadioAvailabilityStatus,
  getRadioAvailabilityStatuses,
  updateRadioAvailabilityStatus,
} from '../../services/admin';

export default function StatusSettingsManager({ orgId }: { orgId: string }) {
  const [statuses, setStatuses] = useState<RadioAvailabilityStatus[]>([]);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState('#0891B2');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [editingColor, setEditingColor] = useState('#0891B2');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const loadStatuses = async () => {
    if (!orgId) {
      setStatuses([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const data = await getRadioAvailabilityStatuses(orgId);
      setStatuses(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load status catalog');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatuses();
  }, [orgId]);

  const handleCreate = async () => {
    const name = newName.trim();
    if (!orgId || !name) return;

    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      await createRadioAvailabilityStatus(orgId, name, newColor);
      setNewName('');
      await loadStatuses();
      setMessage('Status created.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create status');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (id: string) => {
    const name = editingName.trim();
    if (!orgId || !name) return;

    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      await updateRadioAvailabilityStatus(orgId, id, name, editingColor);
      setEditingId(null);
      setEditingName('');
      await loadStatuses();
      setMessage('Status updated.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update status');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!orgId) return;

    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      await deleteRadioAvailabilityStatus(orgId, id);
      await loadStatuses();
      setMessage('Status deleted.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete status');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="max-w-4xl bg-gray-800 border border-gray-700 rounded-lg p-6 space-y-5">
      <div>
        <h2 className="text-2xl font-bold text-white">Status</h2>
        <p className="text-sm text-gray-400 mt-1">Create the operational status names used by radios in this network. The connection state remains separate as the “Online” column.</p>
      </div>

      <div className="flex gap-3">
        <input
          type="color"
          value={newColor}
          onChange={(e) => setNewColor(e.target.value)}
          aria-label="Status color"
          className="h-14 w-14 cursor-pointer rounded border border-gray-600 bg-gray-900 p-1"
        />
        <input
          type="text"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          maxLength={15}
          placeholder="Add status, e.g. Ledig"
          className="flex-1 bg-gray-900 border border-gray-600 rounded-lg px-3 py-2 text-white placeholder-gray-500"
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleCreate();
          }}
        />
        <button
          type="button"
          onClick={handleCreate}
          disabled={saving || !orgId || !newName.trim()}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold rounded-lg"
        >
          {saving ? 'Saving...' : 'Add status'}
        </button>
      </div>

      {error && <p className="text-sm text-red-400">{error}</p>}
      {message && <p className="text-sm text-green-400">{message}</p>}

      {loading ? (
        <p className="text-gray-400">Loading status catalog...</p>
      ) : (
        <div className="border border-gray-700 rounded-lg divide-y divide-gray-700 overflow-hidden">
          {statuses.length === 0 ? (
            <p className="px-4 py-6 text-sm text-gray-400">No status values have been created yet.</p>
          ) : (
            statuses.map((status) => (
              <div key={status.id} className="flex items-center justify-between gap-3 px-4 py-3">
                {editingId === status.id && !status.is_default ? (
                  <>
                    <input
                      type="text"
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      maxLength={15}
                      className="flex-1 bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white"
                    />
                    <input
                      type="color"
                      value={editingColor}
                      onChange={(e) => setEditingColor(e.target.value)}
                      aria-label="Status color"
                      className="h-10 w-12 cursor-pointer rounded border border-gray-600 bg-gray-900 p-1"
                    />
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => handleEdit(status.id)}
                        disabled={saving || !editingName.trim()}
                        className="px-3 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded"
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setEditingId(null);
                          setEditingName('');
                        }}
                        className="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded"
                      >
                        Cancel
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-2">
                      <span className="h-4 w-4 rounded-full border border-white/30" style={{ backgroundColor: status.color }} />
                      <span className="font-medium text-gray-200">{status.name}</span>
                      {status.is_default && <span className="text-xs font-semibold text-cyan-300">Default</span>}
                    </div>
                    <div className="flex gap-2">
                      {!status.is_default && <>
                        <button
                          type="button"
                          onClick={() => {
                            setEditingId(status.id);
                            setEditingName(status.name);
                            setEditingColor(status.color);
                          }}
                          className="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(status.id)}
                          disabled={saving}
                          className="px-3 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded"
                        >
                          Delete
                        </button>
                      </>}
                    </div>
                  </>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </section>
  );
}
